import { useEffect, useRef } from "react";

export default function CTA() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child, i) => {
              setTimeout(() => {
                child.classList.add("animate-fade-in-up");
                child.classList.remove("opacity-0");
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="cta"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden section-padding bg-[#111827]"
    >
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-teal-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      <div className="container-wide relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="opacity-0" data-animate>
            <span className="inline-block px-4 py-1.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-emerald-400 text-xs md:text-sm font-medium tracking-wide mb-6">
              Ready to Build?
            </span>
          </div>

          <h2
            className="opacity-0 font-outfit font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight"
            data-animate
          >
            Start Building Your
            <br />
            <span className="gradient-text">SaaS Today</span>
          </h2>

          <p
            className="opacity-0 text-base md:text-lg text-white/50 max-w-xl mx-auto mb-10 leading-relaxed"
            data-animate
          >
            Whether you need a custom <strong className="text-white/80">SaaS application</strong> or want to integrate <strong className="text-white/80">Translate Cloud API</strong> into your product, our <strong className="text-white/80">SaaS development</strong> team is here to help.
          </p>

          <div
            className="opacity-0 flex flex-col sm:flex-row items-center justify-center gap-4"
            data-animate
          >
            <a
              href="https://forcekeys.com/#contact"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-8 py-3.5 bg-emerald-600 text-white rounded-full font-semibold text-sm md:text-base hover:bg-emerald-700 transition-all duration-300 shadow-lg shadow-emerald-600/20 whitespace-nowrap"
            >
              Contact Our Team
            </a>
            <a
              href="https://support.saas.forcekeys.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-8 py-3.5 bg-white/5 text-white rounded-full font-semibold text-sm md:text-base hover:bg-white/10 transition-all duration-300 border border-white/10 whitespace-nowrap"
            >
              Visit Support Portal
            </a>
          </div>

          <div
            className="opacity-0 mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/30 text-xs md:text-sm"
            data-animate
          >
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-400" />
              </div>
              Free Consultation
            </span>
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-400" />
              </div>
              Dedicated Support
            </span>
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-400" />
              </div>
              Fast Delivery
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
